import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import Flex from '../../../../components/Flex';
import { PrioTheme } from '../../../../theme/types';
import { useTheme } from 'react-jss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { PrioSpinner, PillCRA as Pill } from '@prio365/prio365-react-library';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootReducerState,
  getMonthlyCloseMeByMonth,
  getMonthlyClosesMeInRange,
  getUserMe,
} from '../../../../apps/main/rootReducer';
import { OfficeId } from '../../../../models/Types';
import { apiFetchSearch } from '../../api';
import { useNavigate } from 'react-router-dom';
import { MonthlyClose } from '../../../../models/TimeKeeping';
import moment, { Moment } from 'moment';
import {
  apiFetchEditableMonthlyClose,
  apiFetchMonthlyCloseMeById,
} from '../../../timeKeeping/api';
import { debounceMonthlyCloseMe } from '../../../timeKeeping/actions';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import { useQuery } from '@tanstack/react-query';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    fontSize: theme.font.fontSize.large,
    fontWeight: theme.font.fontWeight.bold,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing.small}px 0px`,
    paddingRight: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,

    backgroundColor: theme.colors.application.background.default,
    '&:hover': {
      backgroundColor: theme.colors.base.primary,
    },
  },
  actionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.regular,
    flex: 1,
  },
  actionTitle: {
    fontSize: theme.font.fontSize.regular,
    fontWeight: theme.font.fontWeight.regular,
    color: theme.colors.application.typography.default,
  },
  actionType: {
    fontSize: theme.font.fontSize.tiny,
    color: theme.colors.application.typography.muted,
    marginTop: theme.spacing.extraSmall,
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    cursor: 'pointer',
  },
  noPendingActionsIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.small,
    borderRadius: '100%',
    border: `1px solid ${theme.colors.base.green.default}`,
    background: theme.colors.base.green[20],
    height: 48,
    width: 48,
  },
  noPendingActionsText: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.small,
  },
  noPendingActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.small,
    justifyContent: 'center',
    height: `calc(100% - ${theme.old.spacing.unit(6)})`,
  },
  centerInRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: 'calc(100% - 44px)',
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.small,
    flex: 1,
    overflow: 'scroll',
  },
}));

type PendingActionType =
  | 'timeKeepingDays'
  | 'monthlyCloseClosures'
  | 'absences'
  | 'timeKeepingEntriesHR'
  | 'compensationPayments';

interface PendingAction {
  iconName: IconName;
  numberOfActions: number;
  type: PendingActionType;
  linkToAction: string;
  label: string;
}

interface DashboardPendingActionsItemProps {
  className?: string;
  height?: number;
}

const generateSearchString = (
  type: PendingActionType,
  officeIdsWhereOfficeHR: OfficeId[]
) => {
  const parsedOfficeIds = officeIdsWhereOfficeHR
    .map((officeId) => `'${officeId}'`)
    .join(',')
    .toString();
  let stateValue = '';
  let officeIdValue = '';

  if (type === 'absences') {
    stateValue = `AbsenceState eq 'requested','revokeRequested'`;
    officeIdValue = `OfficeId eq ${parsedOfficeIds}`;
  }

  if (type === 'timeKeepingEntriesHR') {
    stateValue = `State eq 'ApprovalRequested'`;
    officeIdValue = `OfficeId eq ${parsedOfficeIds}`;
  }

  if (type === 'compensationPayments') {
    stateValue = `IsApproved eq 'false'`;
    officeIdValue = `OfficeId eq ${parsedOfficeIds}`;
  }

  return `${stateValue}${`%26 ${officeIdValue}`}`;
};

const generateLinkToActions = (
  type: PendingActionType,
  userOfficeId: OfficeId,
  officeIdsWhereOfficeHR: OfficeId[],
  closableMonth?: string,
  earliestDayWithoutTimeKeeping?: string
) => {
  const officeId = officeIdsWhereOfficeHR.find(
    (officeId) => officeId === userOfficeId
  )
    ? userOfficeId
    : officeIdsWhereOfficeHR[0];

  let pathPrefix = '/module/prio/hr/';
  let query = `?s=${generateSearchString(type, officeIdsWhereOfficeHR)}`;
  let path = '';
  let pickedFilters = '';

  if (type === 'absences') {
    path = `${`office/${officeId}/`}absence/openproposals`;
    pickedFilters = `&pickedFilters=Data.AbsenceState`;
  }

  if (type === 'timeKeepingEntriesHR') {
    path = `office/${officeId}/timekeeping/timekeepingDays`;
    pickedFilters = `&pickedFilters=Data.State`;
  }

  if (type === 'compensationPayments') {
    path = `office/${officeId}/timekeeping/compensationPayments`;
  }

  if (type === 'monthlyCloseClosures') {
    pathPrefix = '';
    path = `/module/prio/projects/me/timeAndLeaveManagement/summary`;
    query = `?month=${closableMonth}`;
  }

  if (type === 'timeKeepingDays') {
    pathPrefix = '';
    path = `/module/prio/projects/me/timeAndLeaveManagement/summary`;
    query = `?month=${earliestDayWithoutTimeKeeping}`;
  }

  return `${pathPrefix}${path}${query}${pickedFilters}`;
};

const usePendingAction = (
  searchType: string,
  searchString: string,
  isHR: boolean
) => {
  const threshold = 0;
  const forceResult = false;

  const { data, isLoading, isError } = useQuery({
    queryKey: [searchType, searchString, threshold, forceResult],
    queryFn: () =>
      apiFetchSearch(
        searchType ?? '',
        searchString ?? '',
        threshold,
        forceResult
      ),
    staleTime: 1000 * 60 * 60 * 20, // 20 hours
    enabled: isHR,
  });
  const number = useMemo(() => {
    if (isError) {
      return 0;
    }
    return data?.data?.totalItems ?? undefined;
  }, [data, isError]);
  const _isLoading = isHR ? isLoading : false;
  return { number, isLoading: _isLoading };
};

export const DashboardPendingActionsItem: React.FC<
  DashboardPendingActionsItemProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const employeeId = useMemo(() => {
    return userMe?.id;
  }, [userMe?.id]);

  const { getContactById } = useContactsContext();
  const officeId = getContactById(employeeId)?.officeId;

  const officeIdsWhereOfficeHR: OfficeId[] = useMemo(
    () =>
      Object.entries(userMe?.prioData?.officeRoles || {})
        .filter(([_, roles]) => {
          return roles.includes('officeHR');
        })
        .map(([officeId, _]) => officeId),
    [userMe?.prioData?.officeRoles]
  );

  const [earliestMonthToClose, setEarliestMonthToClose] =
    useState<string>(undefined);

  const monthlyClosesMeInRange = useSelector<RootReducerState, MonthlyClose[]>(
    (state) =>
      getMonthlyClosesMeInRange(
        state,
        moment(earliestMonthToClose)?.subtract(1, 'month')?.toISOString(true),
        moment().toISOString(true)
      )
  );

  const currentMonthlyClose = useSelector<RootReducerState, MonthlyClose>(
    (state) =>
      getMonthlyCloseMeByMonth(
        state,
        moment().toISOString(true).substring(0, 7)
      )
  );

  const missingTimeKeepingDays = useMemo(() => {
    const calculateMissingDays = (monthlyCloses: MonthlyClose[]) => {
      if (!monthlyCloses?.[0]) return undefined;
      const number = monthlyCloses.reduce<string[]>((acc, monthlyClose) => {
        const { shouldBeWorkingDays, timeKeepingDays } = monthlyClose;
        const numberOfMissingDays = shouldBeWorkingDays.reduce((acc2, day) => {
          const timeKeepingDay = timeKeepingDays.find((timeKeepingDay) =>
            moment(timeKeepingDay?.timeKeepingEntries[0]?.startTime).isSame(
              day,
              'day'
            )
          );
          if (!timeKeepingDay) {
            acc2.push(day.split('T')[0]);
          }
          return acc2;
        }, [] as string[]);
        return [...acc, ...numberOfMissingDays];
      }, []);
      return number;
    };
    if (
      moment().isSameOrBefore(moment(monthlyClosesMeInRange?.[0]?.month)) &&
      currentMonthlyClose
    ) {
      return calculateMissingDays([currentMonthlyClose]);
    }
    return calculateMissingDays(monthlyClosesMeInRange);
  }, [monthlyClosesMeInRange, currentMonthlyClose]);

  const earliestDayWithoutTimeKeeping = useMemo(() => {
    return missingTimeKeepingDays?.[0]?.substring(0, 7);
  }, [missingTimeKeepingDays]);

  const numberOfOpenTimekeepingDays = useMemo(() => {
    return missingTimeKeepingDays?.length || 0;
  }, [missingTimeKeepingDays]);
  const [
    numberOfMissingMonthlyCloseClosures,
    setNumberOfMissingMonthlyCloseClosures,
  ] = useState<number>(undefined);

  const { number: numberOfPendingAbsenceProposals, isLoading: isApLoading } =
    usePendingAction(
      'absenceProposals',
      generateSearchString('absences', officeIdsWhereOfficeHR),
      officeIdsWhereOfficeHR.length > 0
    );

  const { number: numberOfPendingTimeKeepingEntries, isLoading: isTkLoading } =
    usePendingAction(
      'timekeepingDays',
      generateSearchString('timeKeepingEntriesHR', officeIdsWhereOfficeHR),
      officeIdsWhereOfficeHR.length > 0
    );

  const {
    number: numberOfPendingCompensationPayments,
    isLoading: isCpLoading,
  } = usePendingAction(
    'compensationPayments',
    generateSearchString('compensationPayments', officeIdsWhereOfficeHR),
    officeIdsWhereOfficeHR.length > 0
  );

  const isLoading =
    numberOfOpenTimekeepingDays === undefined ||
    numberOfMissingMonthlyCloseClosures === undefined ||
    isApLoading ||
    isTkLoading ||
    isCpLoading;

  const pendingActions: PendingAction[] = useMemo(
    () =>
      [
        {
          label: t(`dashboard:pendingActions.actions.book`),
          iconName: 'business-time',
          numberOfActions: numberOfOpenTimekeepingDays,
          type: 'timeKeepingDays',
          linkToAction: generateLinkToActions(
            'timeKeepingDays',
            officeId,
            officeIdsWhereOfficeHR,
            undefined,
            earliestDayWithoutTimeKeeping
          ),
        } as PendingAction,
        {
          label: t(`dashboard:pendingActions.actions.close`),
          iconName: 'clock-seven',
          numberOfActions: numberOfMissingMonthlyCloseClosures,
          type: 'monthlyCloseClosures',
          linkToAction: generateLinkToActions(
            'monthlyCloseClosures',
            officeId,
            officeIdsWhereOfficeHR,
            earliestMonthToClose
          ),
        } as PendingAction,
        {
          label: t(`dashboard:pendingActions.actions.approve`),
          iconName: 'island-tropical',
          numberOfActions: numberOfPendingAbsenceProposals,
          type: 'absences',
          linkToAction: generateLinkToActions(
            'absences',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
        {
          label: t(`dashboard:pendingActions.actions.approve`),
          iconName: 'business-time',
          numberOfActions: numberOfPendingTimeKeepingEntries,
          type: 'timeKeepingEntriesHR',
          linkToAction: generateLinkToActions(
            'timeKeepingEntriesHR',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
        {
          label: t(`dashboard:pendingActions.actions.approve`),
          iconName: 'money-bill',
          numberOfActions: numberOfPendingCompensationPayments,
          type: 'compensationPayments',
          linkToAction: generateLinkToActions(
            'compensationPayments',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
      ].filter((pendingAction) => pendingAction?.numberOfActions > 0),
    [
      numberOfOpenTimekeepingDays,
      numberOfMissingMonthlyCloseClosures,
      numberOfPendingAbsenceProposals,
      numberOfPendingTimeKeepingEntries,
      numberOfPendingCompensationPayments,
      officeIdsWhereOfficeHR,
      t,
      earliestDayWithoutTimeKeeping,
      earliestMonthToClose,
      officeId,
    ]
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const debounceFetchMonthlyClose = useCallback(
    (day: Moment) => {
      const start = day.startOf('month').toISOString(true).split('T')[0];
      const end = moment().endOf('month').toISOString(true).split('T')[0];
      dispatch(debounceMonthlyCloseMe(start, end));
    },
    [dispatch]
  );

  const handleActionClick = (action: PendingAction) => {
    navigate(action.linkToAction);
  };

  const renderPill = (type: PendingActionType, number: number) => {
    let icon: IconName;
    switch (type) {
      case 'timeKeepingDays':
        icon = 'business-time';
        break;
      case 'monthlyCloseClosures':
        icon = 'clock-seven';
        break;
      case 'absences':
        icon = 'island-tropical';
        break;
      case 'timeKeepingEntriesHR':
        icon = 'business-time';
        break;
      case 'compensationPayments':
        icon = 'money-bill';
        break;
      default:
        icon = 'exclamation-circle';
    }

    return (
      <Pill style={{ width: 'fit-content' }}>
        <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
          <FontAwesomeIcon icon={['fal', icon]} />
          <span style={{ color: theme.colors.application.typography.default }}>
            {number}
          </span>
        </Flex.Row>
      </Pill>
    );
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (employeeId) {
      const fetchClosableMonthlyClose = async () => {
        try {
          const { data } = await apiFetchEditableMonthlyClose(employeeId);

          if (data) {
            const { nextToCloseMonthlyCloseId } = data;

            const { data: nextToCloseMonthlyClose } =
              await apiFetchMonthlyCloseMeById(nextToCloseMonthlyCloseId);

            if (nextToCloseMonthlyClose) {
              const _diff = moment()
                .endOf('month')
                .diff(moment(nextToCloseMonthlyClose.month), 'month');
              setEarliestMonthToClose(
                moment(nextToCloseMonthlyClose.month)
                  ?.toISOString(true)
                  .substring(0, 7)
              );
              setNumberOfMissingMonthlyCloseClosures(_diff < 0 ? 0 : _diff);
              debounceFetchMonthlyClose(moment(nextToCloseMonthlyClose.month));
            } else {
              setNumberOfMissingMonthlyCloseClosures(0);
            }
          } else {
            setNumberOfMissingMonthlyCloseClosures(0);
          }
        } catch (error) {
          setNumberOfMissingMonthlyCloseClosures(0);
        }
      };
      fetchClosableMonthlyClose();
    }
  }, [employeeId, debounceFetchMonthlyClose]);
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Row childrenGap={theme.spacing.small} marginBottom={8}>
        <Flex.Item>
          <div className={classes.header}>
            {t('dashboard:pendingActions.title')}
          </div>
        </Flex.Item>
      </Flex.Row>

      {isLoading && (
        <div className={classes.centerInRow}>
          <PrioSpinner size="large" />
        </div>
      )}

      {!isLoading && pendingActions.length === 0 && (
        <div className={classes.noPendingActionsContainer}>
          <div className={classes.noPendingActionsIcon}>
            <FontAwesomeIcon
              icon={['fal', 'check']}
              size={'lg'}
              color={theme.colors.base.green.default}
            />
          </div>
          <div className={classes.noPendingActionsText}>
            {t(`dashboard:pendingActions.noActions`)}
          </div>
        </div>
      )}

      {!isLoading && pendingActions.length > 0 && (
        <div className={classes.actionsList}>
          {pendingActions.map((action, index) => (
            <div key={index} className={classes.actionRow}>
              <div className={classes.actionContent}>
                <div>
                  <div className={classes.actionTitle}>
                    {t(`dashboard:pendingActions.type.${action.type}`)}{' '}
                    {action.label}
                  </div>
                  <div className={classes.actionType}>
                    {t(`dashboard:pendingActions.type.desc.${action.type}`)}
                  </div>
                </div>
              </div>
              <div
                className={classes.pillContainer}
                onClick={() => handleActionClick(action)}
              >
                {renderPill(action.type, action.numberOfActions)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Flex.Column>
  );
};

export default DashboardPendingActionsItem;
